import { HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { map } from 'rxjs';

/**
 * Endpoint that lists the checking accounts of a company. Both the bank
 * accounts carousel (NGXS CheckingAccountState) and the accounts list modal
 * (bfm-accounts-modal pagination) are fed by this endpoint.
 */
export const CHECKING_ACCOUNTS_LIST_URL = '/v2/checking_account/by/';

/**
 * Value of `account_type` that identifies a savings account.
 */
export const SAVINGS_ACCOUNT_TYPE = 3;

/**
 * Default nickname displayed for savings accounts without a user-defined one.
 */
export const SAVINGS_ACCOUNT_NICKNAME = 'Conta Poupança';

/**
 * Checking account fields relevant to this interceptor.
 */
interface CheckingAccountLike {
  account_type?: number;
  name?: string | null;
  [key: string]: unknown;
}

/**
 * Checking accounts list response body shape.
 */
interface CheckingAccountsListBody {
  results?: CheckingAccountLike[];
  [key: string]: unknown;
}

/**
 * CAIXA custom layer: savings accounts share agency/account numbers with
 * their sibling checking accounts, which makes them look duplicated. When
 * the back-end returns a savings account (`account_type === 3`) without a
 * user-defined nickname, this interceptor fills the nickname (`name`) with
 * "Conta Poupança" so the carousel and the accounts modal can tell the
 * accounts apart. User-defined nicknames are never overwritten.
 */
export const savingsAccountNicknameInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.includes(CHECKING_ACCOUNTS_LIST_URL)) {
    return next(req);
  }

  return next(req).pipe(
    map((event: HttpEvent<unknown>) => {
      if (!(event instanceof HttpResponse)) {
        return event;
      }

      const body = event.body as CheckingAccountsListBody | null;

      if (!Array.isArray(body?.results)) {
        return event;
      }

      return event.clone({
        body: {
          ...body,
          results: body.results.map((account) =>
            account?.account_type === SAVINGS_ACCOUNT_TYPE && !account.name
              ? { ...account, name: SAVINGS_ACCOUNT_NICKNAME }
              : account,
          ),
        },
      });
    }),
  );
};
